import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { env } from "@/env";
import type { DeliveryTrack } from "@/features/delivery/hooks/use-delivery-tracking";
import { queryKeys } from "@/lib/query-keys";

const ERROR_THRESHOLD = 2;

const RIDER_ACTIVE_STATES = new Set(["accepted", "en_route", "arrived"]);
const RIDER_TERMINAL_STATES = new Set(["delivered", "failed", "cancelled"]);

function isRiderLocationEligible(state: string | null | undefined, acceptedAt: unknown): boolean {
  if (state && RIDER_ACTIVE_STATES.has(state)) {
    return true;
  }
  if (state && RIDER_TERMINAL_STATES.has(state)) {
    if (acceptedAt == null) {
      return false;
    }
    if (acceptedAt instanceof Date) {
      return !Number.isNaN(acceptedAt.getTime());
    }
    return String(acceptedAt).length > 0;
  }
  return false;
}

export type DeliveryEvent = {
  type: "delivery.assigned" | "delivery.state_changed" | "delivery.location_updated";
  timestamp: string;
  organizationId: string;
  orderId: string;
  deliveryId?: string;
  riderId?: string;
  data: {
    deliveryState?: string;
    lat?: number;
    lng?: number;
    capturedAt?: string;
    [key: string]: unknown;
  };
};

export function useDeliveryEvents(
  deliveryId: string | undefined,
  onEvent?: (event: DeliveryEvent) => void,
) {
  const [connected, setConnected] = useState(false);
  const [pollingFallback, setPollingFallback] = useState(false);
  const queryClient = useQueryClient();
  const errorCountRef = useRef(0);
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!deliveryId) {
      return;
    }
    const url = `${env.VITE_API_URL}/api/deliveries/${deliveryId}/events`;
    let stopped = false;

    const eventSource = new EventSource(url, { withCredentials: true });

    eventSource.onopen = () => {
      setConnected(true);
      errorCountRef.current = 0;
    };

    eventSource.onmessage = (e) => {
      try {
        const parsed = JSON.parse(e.data) as DeliveryEvent;

        if (parsed.type === "delivery.state_changed") {
          // Issue #80: refresh cached `state` immediately and invalidate the
          // tracking query so timestamps and origin/destination snapshots are
          // recomputed. We do not block the state update on a refetch.
          const nextState =
            typeof parsed.data.deliveryState === "string" ? parsed.data.deliveryState : undefined;
          queryClient.setQueryData(
            queryKeys.delivery.track(deliveryId),
            (old: DeliveryTrack | undefined) => {
              if (!old || !nextState) {
                return old;
              }
              return { ...old, state: nextState };
            },
          );
          queryClient.invalidateQueries({
            queryKey: queryKeys.delivery.track(deliveryId),
          });
        } else if (parsed.type === "delivery.location_updated") {
          // Issue #80: update local cache fields without invalidating the
          // tracking query on every ping. The backend already enforces the
          // Rider visibility rule, but we apply the same rule defensively
          // here so a stale payload or a race with the cache cannot leak
          // pre-accept coordinates.
          queryClient.setQueryData(
            queryKeys.delivery.track(deliveryId),
            (old: DeliveryTrack | undefined) => {
              if (!old) {
                return old;
              }
              if (!isRiderLocationEligible(old.state, old.acceptedAt)) {
                return old;
              }
              return {
                ...old,
                lastLat: parsed.data.lat != null ? String(parsed.data.lat) : old.lastLat,
                lastLng: parsed.data.lng != null ? String(parsed.data.lng) : old.lastLng,
                lastPingAt: parsed.data.capturedAt ?? old.lastPingAt,
              };
            },
          );
        }

        onEventRef.current?.(parsed);
      } catch {
        // ignore parse errors
      }
    };

    eventSource.onerror = () => {
      setConnected(false);
      errorCountRef.current += 1;
      if (errorCountRef.current >= ERROR_THRESHOLD && !stopped) {
        eventSource.close();
        setPollingFallback(true);
      }
    };

    return () => {
      stopped = true;
      eventSource.close();
    };
  }, [deliveryId, queryClient]);

  return { connected, pollingFallback };
}
